/// <amd-module name="Repos/Discussion/Components/DiscussionThread" />
import * as React from "react";
import { DiscussionStatus, IDiscussionCommentModel } from "Repos/Discussion/Common/DiscussionContracts";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { AddComment } from "./AddComment";
import { DiscussionThreadPermissions, IDiscussionThreadProps } from "./DiscussionThread.Props";
import { PreviewComment } from "./PreviewComment";
import { ReplyComment } from "./ReplyComment";

export const DiscussionThread = (props: IDiscussionThreadProps): JSX.Element => {
    const {
        author,
        className,
        commentEditorClassName,
        getMarkdownEditorProps,
        isShim,
        onAddComment,
        onChangeStatus,
        onDismiss,
        onDismissComment,
        onLikeComment,
        onRemoveComment,
        onUpdateComment,
        permissions = DiscussionThreadPermissions.None,
        personaProvider,
        showLikes,
        thread,
        baseUrl
    } = props;
    const [focusLastComment, setFocusLastComment] = React.useState(false);
    const onAdd = (commentId: number, comment: string, status?: DiscussionStatus): void => {
        onAddComment && onAddComment(commentId, comment);
        setFocusLastComment(true);
        if (status) {
            onChangeStatus && onChangeStatus(status);
        }
    };

    const onUpdate = (commentId: number, comment: string, status?: DiscussionStatus): void => {
        onUpdateComment && onUpdateComment(commentId, comment);

        if (status) {
            onChangeStatus && onChangeStatus(status);
        }
    };

    const canEdit = (permissions & DiscussionThreadPermissions.ManageComment) > 0;

    return (
        <div className={css(className, "repos-discussion-thread flex-column flex-grow scroll-hidden")}>
            <Observer error={thread.getError()}>
                {(props: { error: Error | null }) => {
                    return props.error ? (
                        <MessageCard
                            onDismiss={() => {
                                thread.setError(null);
                            }}
                            severity={MessageCardSeverity.Error}
                        >
                            {props.error.message}
                        </MessageCard>
                    ) : null;
                }}
            </Observer>
            <Observer comments={thread.getComments()}>
                {(props: { comments: IDiscussionCommentModel[] }) => {
                    const savedComments = props.comments.filter(c => c.getId() > 0);
                    if (savedComments.length > 0) {
                        const lastCommentIndex = savedComments.length - 1;
                        // Show comments since this is an existing thread with (possible) comments

                        return (
                            <>
                                {savedComments.map((comment, index) => {
                                    return (
                                        <PreviewComment
                                            author={author}
                                            comment={comment}
                                            commentEditorClassName={commentEditorClassName}
                                            markdownEditorProps={getMarkdownEditorProps && getMarkdownEditorProps(comment.getId())}
                                            first={index === 0}
                                            focusEditButton={index === lastCommentIndex ? focusLastComment : false}
                                            hasReplies={savedComments.length > 1}
                                            isShim={isShim}
                                            key={`discussion-comment-${thread.id}-${comment.getId()}`}
                                            onChangeStatus={onChangeStatus}
                                            onLikeComment={onLikeComment}
                                            onRemoveComment={onRemoveComment}
                                            onUpdateComment={onUpdate}
                                            permissions={permissions}
                                            personaProvider={personaProvider}
                                            showLikes={showLikes}
                                            status={thread.getStatus()}
                                            baseUrl={baseUrl}
                                        />
                                    );
                                })}
                                {canEdit && (
                                    <ReplyComment
                                        author={author}
                                        className="repos-discussion-thread-reply"
                                        comment={thread.getInputComment()}
                                        commentEditorClassName={commentEditorClassName}
                                        key={`discussion-reply-${thread.id}`}
                                        markdownEditorProps={getMarkdownEditorProps && getMarkdownEditorProps(-1)}
                                        onCancel={() => {
                                            onDismissComment && onDismissComment(thread.id);
                                            !thread.hasComments() && onDismiss && onDismiss(thread.id);
                                        }}
                                        onChangeStatus={onChangeStatus}
                                        onReplyComment={onAdd}
                                        personaProvider={personaProvider}
                                        status={thread.getStatus()}
                                    />
                                )}
                            </>
                        );
                    } else if (canEdit) {
                        // Show add comment since the thread doesn't have any comments yet
                        return (
                            <AddComment
                                author={author}
                                className={css("repos-discussion-thread-reply", commentEditorClassName)}
                                comment={thread.getInputComment()}
                                key={`discussion-add-comment-${thread.id}`}
                                markdownEditorProps={getMarkdownEditorProps && getMarkdownEditorProps(-1)}
                                onCancel={() => {
                                    onDismissComment && onDismissComment(thread.id);
                                    onDismiss && onDismiss(thread.id);
                                }}
                                onComment={onAdd}
                                personaProvider={personaProvider}
                            />
                        );
                    }

                    // There are no (undeleted) comments to show and user cannot edit, so returning null
                    // since the user cannot start a new discussion thread. Ideally, consumer should
                    // not render this component at all if there are no comments in a thread and editing is not allowed.
                    return null;
                }}
            </Observer>
        </div>
    );
};
